import type { ScheduleMonth } from '../../lib/calc/engine';
import { financialYearOf } from '../../lib/calc/dates';

export interface FinancialYearGroup {
  financialYear: string; // e.g. "2026-27"
  months: ScheduleMonth[];
  totalEmiPaid: number;
  totalInterest: number;
  totalPrincipalViaEmi: number;
  totalPrepayment: number;
  totalPrincipalReduction: number;
  totalPayment: number;
  openingBalance: number;
  closingBalance: number;
}

/**
 * Groups a flat ScheduleMonth[] (already computed by the real engine —
 * this function does NOT calculate anything itself, purely a
 * presentation-layer grouping) into Indian Financial Year buckets
 * (April–March), each with its own summary row, for the Schedule
 * screen's FY-collapsible / month-expandable view.
 */
export function groupByFinancialYear(months: ScheduleMonth[]): FinancialYearGroup[] {
  const groups = new Map<string, ScheduleMonth[]>();

  for (const month of months) {
    const fy = financialYearOf(month.emiDate);
    const existing = groups.get(fy);
    if (existing) {
      existing.push(month);
    } else {
      groups.set(fy, [month]);
    }
  }

  return Array.from(groups.entries()).map(([financialYear, fyMonths]) => {
    const totalEmiPaid = fyMonths.reduce((sum, m) => sum + m.emiAmount, 0);
    const totalInterest = fyMonths.reduce((sum, m) => sum + m.interestComponent, 0);
    const totalPrincipalViaEmi = fyMonths.reduce((sum, m) => sum + m.principalComponent, 0);
    const totalPrepayment = fyMonths.reduce((sum, m) => sum + m.prepaymentAmount, 0);
    return {
      financialYear,
      months: fyMonths,
      totalEmiPaid,
      totalInterest,
      totalPrincipalViaEmi,
      totalPrepayment,
      totalPrincipalReduction: totalPrincipalViaEmi + totalPrepayment,
      totalPayment: totalEmiPaid + totalPrepayment,
      openingBalance: fyMonths[0].openingBalance,
      closingBalance: fyMonths[fyMonths.length - 1].closingBalance,
    };
  });
}
